/*
**  rui-tab.js
**
**  rui-tab component class
*/
import { LitElement, html, css } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
export class RuiTab extends LitElement {
    static get is() {
        return 'rui-tab';
    }
    static get properties() {
        return {
            labelText: { type: String },
            badge: { type: String },
            isDisabled: { type: Object },
            _isActive: { type: Boolean, attribute: false }
        };
    }
    constructor() {
        super();
        this._labelText = '';
        this._badge = '';
        this._isDisabled = false;
        // Generate id that should be unique
        this._id = Math.floor(Math.random() * Date.now());
        this._isActive = false;
    }
    // Custom setter for labelText property
    set labelText(newValue) {
        if (this._labelText !== newValue) {
            // Store new value
            this._labelText = newValue;
            // Fire tab updated event
            this.fireTabUpdated();
        }
    }
    // Custom getter for labelText property
    get labelText() {
        return this._labelText;
    }
    // Custom setter for badge property
    set badge(newValue) {
        if (this._badge !== newValue) {
            // Store new value
            this._badge = newValue;
            // Fire tab updated event
            this.fireTabUpdated();
        }
    }
    // Custom getter for badge property
    get badge() {
        return this._badge;
    }
    // Custom setter for isDisabled property
    set isDisabled(newValue) {
        if (this._isDisabled !== newValue) {
            // Store new value
            this._isDisabled = newValue;
            // Fire tab updated event
            this.fireTabUpdated();
        }
    }
    // Custom getter for isDisabled property
    get isDisabled() {
        return this._isDisabled;
    }
    fireTabUpdated() {
        Promise.resolve().then(() => {
            let tabUpdatedEvent = new CustomEvent('rui-tab-updated', {
                detail: {
                    id: this._id,
                    labelText: this._labelText,
                    badge: this._badge,
                    isDisabled: this._isDisabled
                },
                bubbles: true,
                composed: true
            });
            this.dispatchEvent(tabUpdatedEvent);
        });
    }
    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('rui-tab-selected', (e) => this.tabSelected(e));
        let tabNewEvent = new CustomEvent('rui-tab-new', {
            detail: {
                id: this._id,
                labelText: this._labelText,
                badge: this._badge,
                isDisabled: this._isDisabled
            },
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(tabNewEvent);
    }
    disconnectedCallback() {
        window.removeEventListener('rui-tab-selected', (e) => this.tabSelected(e));
        super.disconnectedCallback();
    }
    click(e) {
        if (!this._isDisabled) {
            let valueUpdateEvent = new CustomEvent('rui-tab-selected', {
                detail: this.value,
                bubbles: true,
                composed: true
            });
            this.dispatchEvent(valueUpdateEvent);
        }
    }
    tabSelected(e) {
        this._isActive = (e.detail == this._id);
        this.requestUpdate();
    }
    render() {
        const classes = {
            ['rui-tab']: true,
            ['rui-tab-active']: this._isActive
        };
        return html `
            <div id="rui-tab-content-${this._id}" class="${classMap(classes)}" role="tabpanel" aria-labelledby="rui-tab-label-${this._id}">
                <slot></slot>
            </div>
        `;
    }
    static get styles() {
        return css `
            .rui-tab {
                display: none;
                font-family: var(--font-family, "Segoe UI", "Open Sans", "Helvetica Neue", Arial, sans-serif);
                font-size: var(--btn-font-size-md, 14px);
                font-weight: var(--btn-font-weight, 400);
                line-height: var(--btn-line-height-md, 1.42857143);
                padding: 5px;
            }
            
            .rui-tab-active {
                display: block;
            }
        `;
    }
}
customElements.get(RuiTab.is) || customElements.define(RuiTab.is, RuiTab);
